/**
 * The product's answers: the backend's own pages, totals and single-record read.
 *
 * What each of these replaces is a number the browser used to compute from whatever it had already
 * downloaded. The register's page is the server's page, its count is the server's count, the panel's
 * totals are counted where the records are, and a record is found by asking for it rather than by
 * hoping it is in a list that was fetched earlier.
 *
 * Failures are NOT converted into emptiness. Each method lets the error through (or, for the
 * single-record lookup, names it), and `useRead` turns it into the session / forbidden / server
 * distinction the screen shows. That is the whole point: «the server did not answer» must never draw
 * the same screen as «there is nothing here».
 */
import type { ApiClient } from '../../api/client';
import type { CredentialGroupView } from '../../api/types';
import { isApiError } from '../../api/errors';
import {
  listAssets,
  listCredentialGroups,
  listBatches,
  listCredentials,
  listFonts,
  readAssetContent,
  readAssetUsages,
  readBatch,
  readBatchRows,
  readCredential,
  readCredentialByCode,
  readCredentialRegisterSummary,
  readFontContent,
} from '../../api/internal';
import { libraryAssetFromApi, libraryFontFromApi, loadFontFace } from '../lib/asset-library';
import { assetById, type RenderAsset, type RenderFont, registerRenderLibrary } from '../lib/render/library';
import { batchFromApi, batchRowFromApi, credentialFromListRow, hydrateCredentialFromApi } from './from-api';
import type {
  AssetLibrary,
  AssetUsageSummary,
  BatchLookup,
  BatchPage,
  BatchQuery,
  CredentialLookup,
  PreviewFailure,
  RegisterPage,
  RegisterQuery,
  RegisterSummary,
  RowWindow,
  V07Queries,
} from './queries';
import type { DemoBatchRow, DemoCredential, DemoDatabase, DemoRelease } from './types';

/**
 * The page size for an open batch's rows — the reference's own cap, now a page instead of a ceiling.
 *
 * The reference drew at most a hundred and twenty rows and said «… N satır daha» about the rest,
 * which was true and unreachable. The same hundred and twenty is a page now.
 */
export const ROWS_PER_PAGE = 120;
/** Rejected rows are read in bigger bites: the file is written once, from all of them. */
const ERROR_ROWS_PER_READ = 500;

/**
 * Why one row's bytes did not arrive — and the one case that is not about a row at all.
 *
 * A refused read (403) is this session's role; a 404 is content the server cannot find behind a row it
 * listed; anything else is a fault that may pass. None of the three is a reason to lose the library:
 * the rest of the rows were listed and read, and a grid that empties itself because one thumbnail
 * broke tells an administrator their files are gone.
 *
 * 401 is deliberately NOT one of these. It means the session ended between the list and the bytes, and
 * that is the shell's own flow to run — degrading it to «bu önizleme okunamadı» would hide the fact
 * that nothing else on the page can be trusted either. So it is re-thrown and the read fails as a
 * session failure, which is what it is.
 */
function previewFailureOf(e: unknown): PreviewFailure {
  if (isApiError(e)) {
    if (e.status === 401) throw e;
    if (e.status === 403) return 'forbidden';
    if (e.status === 404) return 'missing';
  }
  return 'unreadable';
}

/** The register's own state vocabulary; anything else the toolbar holds is a stored state. */
const VIEW_STATES = new Set([
  'pending_acceptance',
  'pending_admin_publish',
  'active',
  'expired',
  'revoked',
  'superseded',
]);

export function createApiQueries(api: ApiClient, read: () => DemoDatabase): V07Queries {
  /** Releases are loaded whole (a credential renders from its own release), so this is a lookup. */
  const releaseIndex = (): Map<string, DemoRelease> => {
    const index = new Map<string, DemoRelease>();
    for (const t of read().templates) for (const r of t.releases) index.set(r.id, r);
    return index;
  };

  const pagesOf = (total: number, perPage: number) => Math.max(1, Math.ceil(total / perPage));

  return {
    async registerPage(query: RegisterQuery): Promise<RegisterPage> {
      const releases = releaseIndex();
      const filter = {
        ...(query.q.trim() ? { q: query.q.trim() } : {}),
        ...(query.status && VIEW_STATES.has(query.status) ? { status: query.status } : {}),
        ...(query.template ? { templateId: query.template } : {}),
        ...(query.delivery ? { delivery: query.delivery } : {}),
        ...(query.batch ? { batchId: query.batch } : {}),
        ...(query.group ? { groupId: query.group } : {}),
        ...(query.noEmail ? { noEmail: true } : {}),
      };
      const filtered = Object.keys(filter).length > 0;
      const perPage = query.perPage;
      const wanted = Math.max(query.page, 1);

      /*
       * The unfiltered denominator, asked for in the same round trip rather than in a second one —
       * and only when a filter is actually narrowing the set, because without one the page's own
       * total already IS the denominator.
       */
      const [page, all] = await Promise.all([
        listCredentials(api, { ...filter, limit: perPage, offset: (wanted - 1) * perPage }),
        filtered ? listCredentials(api, { limit: 1 }) : Promise.resolve(null),
      ]);

      const pages = pagesOf(page.total, perPage);
      /*
       * A page past the end answers with the last page that exists instead of an empty table. It
       * happens whenever a filter narrows the set while the reader is on page nine, and an empty
       * table there reads as «your records are gone».
       */
      if (wanted > pages && page.total > 0) {
        const last = await listCredentials(api, { ...filter, limit: perPage, offset: (pages - 1) * perPage });
        return {
          rows: last.credentials.map((row) =>
            credentialFromListRow(row, releases.get(row.templateReleaseId) ?? null),
          ),
          filtered: last.total,
          total: all?.total ?? last.total,
          page: pages,
          pages,
        };
      }
      return {
        rows: page.credentials.map((row) =>
          credentialFromListRow(row, releases.get(row.templateReleaseId) ?? null),
        ),
        filtered: page.total,
        total: all?.total ?? page.total,
        page: Math.min(wanted, pages),
        pages,
      };
    },

    async batchPage(query: BatchQuery): Promise<BatchPage> {
      const releases = releaseIndex();
      const perPage = query.perPage;
      const wanted = Math.max(query.page, 1);
      let page = await listBatches(api, { limit: perPage, offset: (wanted - 1) * perPage });
      let pages = pagesOf(page.total, perPage);
      /*
       * A page past the end answers with the last page that EXISTS — its rows, not just its number.
       *
       * Reporting `page: 3` of 3 while returning the rows of page 9 (none) was worse than either
       * honest answer: the screen said «Sayfa 3 / 3» over an empty table, so an upload history with
       * fifty-five entries in it read as «Henüz yükleme yok» to anyone who had paged too far.
       */
      if (wanted > pages && page.total > 0) {
        page = await listBatches(api, { limit: perPage, offset: (pages - 1) * perPage });
        pages = pagesOf(page.total, perPage);
      }
      const summaries = new Map(page.summaries.map((s) => [s.batchId, s]));
      return {
        /*
         * No rows. The history prints four numbers per batch and the server counted them; the rows
         * themselves belong to the batch that is actually open, and are read there.
         */
        rows: page.batches.map((b) =>
          batchFromApi(b, [], releases.get(b.templateReleaseId) ?? null, null, summaries.get(b.id) ?? null),
        ),
        total: page.total,
        /* The number and the rows are the same page, by construction: both come from `page`. */
        page: page.total === 0 ? 1 : Math.floor(page.offset / perPage) + 1,
        pages,
      };
    },

    async registerSummary(): Promise<RegisterSummary> {
      const s = await readCredentialRegisterSummary(api);
      return {
        total: s.total,
        byState: s.byState,
        byTemplate: s.byTemplate,
        issuedThisMonth: s.issuedThisMonth,
        expiringSoon: s.expiringWithinHorizon,
        bounced: s.delivery.bounced ?? 0,
        queued: s.delivery.queued ?? 0,
      };
    },

    async credentialGroups(): Promise<CredentialGroupView[]> {
      const r = await listCredentialGroups(api);
      return r.groups;
    },

    async credentialByCode(code: string): Promise<CredentialLookup> {
      const wanted = code.trim();
      if (!wanted) return { kind: 'not_found' };
      try {
        /*
         * The AUTHORIZED internal read, in two steps, because the internal routes address records by
         * id and every surface addresses them by public code. The anonymous public projection is
         * deliberately not involved: it is a different, narrower view and it is not management data.
         *
         * The first step RESOLVES the code — `/credentials/by-code/:code`, the same `credential.read`
         * cell — instead of searching for it. The register's free-text `q` also matches holder names
         * and contact addresses, so an exact code could sit behind any number of incidental matches
         * and come back «not found» about a record that exists.
         */
        const { credential: row } = await readCredentialByCode(api, wanted);
        const releases = releaseIndex();
        const base: DemoCredential = credentialFromListRow(row, releases.get(row.templateReleaseId) ?? null);
        const detail = await readCredential(api, row.id);
        return {
          kind: 'found',
          /* Events and the delivery state arrive with the detail hydration the screen runs next. */
          credential: hydrateCredentialFromApi(base, detail, [], row.deliveryStatus ?? null),
        };
      } catch (e) {
        /*
         * A refusal and an absence are ANSWERS and replace what is on screen. Anything else — a 500, a
         * 503, a dropped connection — is the read failing, and is thrown so `useRead` can keep the
         * previous answer and mark it as previous. Swallowing it into a verdict made «this is stale»
         * unsayable.
         */
        if (isApiError(e)) {
          if (e.status === 403) return { kind: 'forbidden' };
          if (e.status === 404) return { kind: 'not_found' };
        }
        throw e;
      }
    },

    /*
     * One batch: its header with the server's own counts, and ONE page of its rows.
     *
     * Two requests, whatever the batch's size — the counts are a grouped count on the server and the
     * rows arrive with the credential each one produced, so nothing here depends on the register
     * having been visited and nothing grows per row.
     */
    async batchById(id: string, rows?: RowWindow): Promise<BatchLookup> {
      const window = rows ?? { limit: ROWS_PER_PAGE, offset: 0 };
      try {
        const [detail, page] = await Promise.all([
          readBatch(api, id),
          readBatchRows(api, id, { limit: window.limit, offset: window.offset }),
        ]);
        const releases = releaseIndex();
        /*
         * Asked past the end — the row count shrank between two reads, say. Answer with the last page
         * that exists rather than an empty list under a page number nothing is on.
         */
        const settled =
          page.total > 0 && window.offset >= page.total
            ? await readBatchRows(api, id, {
                limit: window.limit,
                offset: Math.max(0, (Math.ceil(page.total / window.limit) - 1) * window.limit),
              })
            : page;
        return {
          kind: 'found',
          batch: batchFromApi(
            detail.batch,
            settled.rows,
            releases.get(detail.batch.templateReleaseId) ?? null,
            detail.outcome ?? null,
            detail.summary ?? null,
            { total: settled.total, offset: settled.offset },
          ),
        };
      } catch (e) {
        /* As above: verdicts are returned, failures are thrown. */
        if (isApiError(e)) {
          if (e.status === 403) return { kind: 'forbidden' };
          if (e.status === 404) return { kind: 'not_found' };
        }
        throw e;
      }
    },

    /*
     * The library, from the backend, with the bytes it is allowed to read.
     *
     * Three things in one read, because the library screen and the background picker need all three:
     * the metadata (`template.read`, every role), the bytes of each image (`asset.read`, owner/admin,
     * turned into an object URL so a private bucket is never exposed), and each font's bytes loaded
     * into a real `FontFace` so the editor measures and draws with the face a release binds to rather
     * than with whatever the browser would substitute.
     *
     * Everything fetched is also registered in the render library, which is what the render plan
     * resolves against — fail-closed on the digest. An asset already registered (the provider fetches
     * the ones a drawing references before any screen mounts) is not fetched twice.
     */
    async assetLibrary(): Promise<AssetLibrary> {
      const [assetRows, fontRows] = await Promise.all([
        listAssets(api).then((r) => r.assets),
        listFonts(api).then((r) => r.fonts),
      ]);

      const previews: Record<string, PreviewFailure> = {};
      const assets = await Promise.all(
        assetRows.map(async (row) => {
          const known = assetById(row.id);
          if (known !== null && known.sha256 === row.sha256 && known.dataUrlForPrototype) {
            return libraryAssetFromApi(row, known.dataUrlForPrototype);
          }
          try {
            const { objectUrl } = await readAssetContent(api, row.id);
            return libraryAssetFromApi(row, objectUrl);
          } catch (e) {
            previews[row.id] = previewFailureOf(e);
            return libraryAssetFromApi(row, '');
          }
        }),
      );

      const fonts = await Promise.all(
        fontRows.map(async (row) => {
          try {
            const { bytes } = await readFontContent(api, row.id);
            const state = await loadFontFace(row.family, bytes, { weight: row.weight, style: row.style });
            return libraryFontFromApi(row, state);
          } catch (e) {
            previews[row.id] = previewFailureOf(e);
            /* Registered, listed, and honestly marked as a face this session cannot load. */
            return libraryFontFromApi(row, 'pending');
          }
        }),
      );

      registerRenderLibrary({
        assets: assets
          .filter((a) => a.dataUrlForPrototype !== '')
          .map((a) => ({ ...a }) satisfies RenderAsset),
        fonts: fonts.map((f) => ({
          id: f.id,
          sha256: f.sha256,
          family: f.family,
          weight: f.weight,
          style: f.style,
        })) satisfies RenderFont[],
      });

      return { assets, fonts, previews, origin: 'server' };
    },

    /*
     * One question per picture, four at a time.
     *
     * There is a per-asset route and it answers exactly the question the tile asks, so no new bulk
     * endpoint was added for this: the fan-out is bounded here instead. Four in flight keeps a
     * library of a few hundred images from opening a few hundred sockets at once while still
     * finishing in a handful of round trips.
     *
     * Nothing is cached inside this function on purpose. `useRead` already keeps the answer for as
     * long as the question is unchanged, and it re-asks the moment a command changed the world — so
     * a cache here would survive «uygula», «kaldır» and «yayınla» and go on showing a count the
     * backend no longer agrees with.
     *
     * A read that fails is not turned into a zero. It is left OUT of the map, and the tile says it
     * could not read the usage rather than claiming the picture is unused — which is the difference
     * between «nobody needs this» and «the server did not answer», and the delete conversation is
     * exactly where those must not be confused.
     */
    async assetUsage(assetIds: string[]): Promise<Record<string, AssetUsageSummary>> {
      const out: Record<string, AssetUsageSummary> = {};
      const queue = [...assetIds];
      const worker = async (): Promise<void> => {
        for (;;) {
          const id = queue.shift();
          if (id === undefined) return;
          try {
            const u = await readAssetUsages(api, id);
            /* A template is counted ONCE however many places inside it draw the picture. */
            const templates = new Set<string>();
            for (const d of u.drafts) templates.add(d.templateId);
            for (const r of u.releases) templates.add(r.templateId);
            out[id] = {
              templates: templates.size,
              manifests: u.manifestCount,
              deletable: u.deletable,
            };
          } catch {
            /* Deliberately absent rather than zero; the screen reports it as unread. */
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, queue.length) }, worker));
      return out;
    },

    /*
     * Every rejected row, across as many pages as it takes — on the click that asks for it.
     *
     * The backend has two words for «rejected» (`needs_resolution` and `failed`), so both are read.
     * The loop is bounded by the number of rejected rows rather than by the batch's size, and a read
     * that fails throws: the caller says so instead of writing a file that looks complete.
     */
    async batchErrorRows(id: string): Promise<DemoBatchRow[]> {
      const out: DemoBatchRow[] = [];
      /* The run's own column mapping, so the rows the CSV is built from carry the same names the
         screen's rows do rather than only the file's headers. */
      const mapping = await readBatch(api, id)
        .then((r) => r.batch.columnMapping)
        .catch(() => null);
      for (const status of ['needs_resolution', 'failed'] as const) {
        let offset = 0;
        for (;;) {
          const page = await readBatchRows(api, id, { status, limit: ERROR_ROWS_PER_READ, offset });
          for (const r of page.rows) out.push(batchRowFromApi(r, mapping));
          offset += page.rows.length;
          if (page.rows.length === 0 || offset >= page.total) break;
        }
      }
      return out.sort((a, b) => Number(a.n ?? 0) - Number(b.n ?? 0));
    },
  };
}
